import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import CampaignConstants from 'pages/admin/campaign/constants';
import { ICampaignType, IListCampaignsSearchForm } from 'types/campaign';
import { ICampaignState } from 'types/type.global';
import { IQuestionNewEditForm } from 'types/campaign-design';

const initialState: ICampaignState = {
    campaigns: [],
    campaign: null,
    campaignsSearchForm: {
        searchKeyWord: '',
        status: [CampaignConstants.status.ALL],
        campaignType: [CampaignConstants.service.ALL],
        startDate: '',
        endDate: '',
    },
    campaignOverviewSearchForm: {
        service: [CampaignConstants.service.ALL],
        device: [CampaignConstants.device.ALL],
        area: [CampaignConstants.area.ALL],
        startDate: '',
        endDate: '',
    },
    listCampaignsSearchForm: [],
    selectedQuestionIndex: null,
    campaignQuestions: [],
};

const campaignsSlice = createSlice({
    name: 'campaigns',
    initialState,
    reducers: {
        setCampaigns: (state, action: PayloadAction<ICampaignType[]>) => {
            state.campaigns = action.payload;
        },
        clearCampaigns: (state) => {
            state.campaigns = [];
        },
        setCampaign: (state, action: PayloadAction<ICampaignType>) => {
            state.campaign = action.payload;
        },
        clearCampaign: (state) => {
            state.campaign = null;
        },
        setCampaignsSearchForm: (
            state,
            action: PayloadAction<IListCampaignsSearchForm>
        ) => {
            state.campaignsSearchForm = {
                ...state.campaignsSearchForm,
                ...action.payload,
            };
        },
        clearCampaignsSearchForm: (state) => {
            state.campaignsSearchForm = initialState.campaignsSearchForm;
        },
        setCampaignOverviewSearchForm: (
            state,
            action: PayloadAction<IListCampaignsSearchForm>
        ) => {
            state.campaignOverviewSearchForm = {
                ...state.campaignOverviewSearchForm,
                ...action.payload,
            };
        },
        clearCampaignOverviewSearchForm: (state) => {
            state.campaignOverviewSearchForm =
                initialState.campaignOverviewSearchForm;
        },
        clearCampaignsSearchFormFilter: (state) => {
            state.campaignsSearchForm = {
                ...state.campaignsSearchForm,
                status: [],
                campaignType: [],
                startDate: '',
                endDate: '',
            };
        },
        setListCampaignsSearchForm: (
            state,
            action: PayloadAction<ICampaignType[]>
        ) => {
            state.listCampaignsSearchForm = action.payload;
        },
        clearListCampaignsSearchForm: (state) => {
            state.listCampaignsSearchForm = [];
        },
        setSelectedQuestionIndex: (
            state,
            action: PayloadAction<number | null>
        ) => {
            state.selectedQuestionIndex = action.payload;
        },
        pushNewCreatedCampaignToTopOfList: (
            state,
            action: PayloadAction<ICampaignType>
        ) => {
            if (state.campaigns.length >= 10) {
                state.campaigns.pop();
            }
            state.campaigns.unshift(action.payload);
        },
        setCampaignQuestions: (
            state,
            action: PayloadAction<IQuestionNewEditForm[]>
        ) => {
            state.campaignQuestions = action.payload;
        },
        clearCampaignQuestions: (state) => {
            state.campaignQuestions = [];
        },
        removeSkipLogicFromQuestionAffected: (
            state,
            action: PayloadAction<{
                listAffected: {
                    questionId?: number | null | string;
                    skipId?: number | null | string;
                }[];
            }>
        ) => {
            const { listAffected } = action.payload;
            state.campaignQuestions = state.campaignQuestions.map(
                (question) => {
                    const affectedQuestion = listAffected.find(
                        (item: {
                            questionId?: number | null | string;
                            skipId?: number | null | string;
                        }) => item.questionId === question.id
                    );
                    if (
                        affectedQuestion &&
                        question.skipLogic &&
                        question.skipLogic.length > 0
                    ) {
                        return {
                            ...question,
                            skipLogic: question.skipLogic.filter(
                                (skipLogic) =>
                                    skipLogic.id !==
                                    Number(affectedQuestion.skipId)
                            ),
                        };
                    }
                    return question;
                }
            );
        },
    },
});

export const {
    setCampaigns,
    clearCampaigns,
    setCampaign,
    clearCampaign,
    setCampaignsSearchForm,
    clearCampaignsSearchFormFilter,
    clearCampaignsSearchForm,
    setListCampaignsSearchForm,
    setSelectedQuestionIndex,
    pushNewCreatedCampaignToTopOfList,
    setCampaignOverviewSearchForm,
    clearCampaignOverviewSearchForm,
    setCampaignQuestions,
    clearCampaignQuestions,
    removeSkipLogicFromQuestionAffected,
} = campaignsSlice.actions;

export default campaignsSlice.reducer;
